/**
 * Shared chrome for settings sections: section header, group headings,
 * loading placeholder, and the studio field label. There are no save
 * buttons anywhere in settings — every control commits itself.
 *
 * The shapes these draw live in `settings.module.css`, the settings
 * vocabulary every section reads from; this file is only what renders them.
 * `labelStyles` — the exported Tailwind string every settings form label
 * used to carry — is gone: the label shape is `components/ui/field.module.css`'s
 * `.label`, the same rule as `web/`'s own copy, because a field label that
 * differed between the workspace and a Customer's page is exactly the drift
 * DESIGN-SYSTEM.md's opening rule exists to stop.
 */
import forms from "../../components/ui/field.module.css";
import { Tooltip } from "../../components/ui/Tooltip";
import { ChevronLeftIcon, ChevronRightIcon } from "../../components/ui/icons";
import styles from "./settings.module.css";

/**
 * Studio field label. An explanation hangs off the label text itself — there is
 * no info glyph beside it (DESIGN-SYSTEM.md, Tooltips).
 */
export function FieldLabel({
  htmlFor,
  tooltip,
  children,
}: {
  htmlFor: string;
  /** One sentence explaining what the field's value affects. */
  tooltip?: string;
  children: React.ReactNode;
}) {
  // The Tooltip wraps the label rather than sitting inside it: Astryx keeps the
  // (closed) bubble in the DOM, and inside the label its text would join the
  // field's accessible name.
  const label = (
    <label className={forms.label} htmlFor={htmlFor}>
      {children}
    </label>
  );
  // inline-block on the trigger: it wraps a block-level <label>, and an inline
  // span around a block child has no usable box of its own — Astryx measures
  // that empty rect and lands the bubble away from the label it describes.
  return tooltip ? (
    <Tooltip content={tooltip} className={styles.tooltipTrigger}>
      {label}
    </Tooltip>
  ) : (
    label
  );
}

/** Parse a non-negative number input; empty/invalid → null. */
export function numOrNull(s: string): number | null {
  if (s.trim() === "") return null;
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function Loading() {
  return <p className={styles.loading}>Loading…</p>;
}

/**
 * A row that opens its own space: title, one-line description, right chevron.
 * The drill-down gallery pattern from Documents & emails — a section whose
 * contents are too big to sit on one screen lists its destinations instead.
 */
export function DrillDownRow({
  name,
  description,
  onOpen,
}: {
  name: string;
  description: string;
  onOpen: () => void;
}) {
  return (
    <li>
      <button type="button" onClick={onOpen} className={styles.drill}>
        <span className={styles.drillText}>
          <span className={styles.drillName}>{name}</span>
          <span className={styles.drillDescription}>{description}</span>
        </span>
        <ChevronRightIcon className={styles.drillChevron} />
      </button>
    </li>
  );
}

/** The back link above a drilled-into space. */
export function BackLink({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick} className={styles.backLink}>
      <ChevronLeftIcon className={styles.backIcon} />
      {label}
    </button>
  );
}

export function SectionHeader({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: React.ReactNode;
}) {
  return (
    <div className={styles.header}>
      <div className={styles.headerText}>
        <h2 className={styles.headerTitle}>{title}</h2>
        <p className={styles.headerDescription}>{description}</p>
      </div>
      {action}
    </div>
  );
}

export function SettingsGroup({
  id,
  title,
  description,
  action,
  children,
}: {
  id: string;
  title: string;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section aria-labelledby={id} className={styles.group}>
      <div className={styles.groupHead}>
        <div className={styles.headerText}>
          <h3 id={id} className={styles.groupTitle}>
            {title}
          </h3>
          {description && (
            <p className={styles.groupDescription}>{description}</p>
          )}
        </div>
        {action}
      </div>
      <div className={styles.groupBody}>{children}</div>
    </section>
  );
}
